import { BaseViewStore } from 'core/stores';
import { FulfillmentOrderStore } from 'modules/order/stores';
import { MarkAsShippedBulkForm } from 'modules/order/forms';
import { observable, action, makeObservable } from 'mobx';
import { ApplicationStore } from 'platform/application/stores';
import { notificationError, isSome, translate } from 'core/utils';
import { CarrierServiceStore } from 'modules/carrier/stores';
import _ from 'lodash';
import { sourceOfChange } from 'common/constants';

class MarkAsShippedBulkViewStore extends BaseViewStore {
	parentView = null;
	@observable selectedWarehouse = null;
	@observable warehouseOptions = [];
	@observable selectedCarrierService = null;
	@observable carrierServiceOptions = [];

	form = new MarkAsShippedBulkForm({
		onSuccess: async () => {
			await this.markAsShippedBulk();
		},
	});

	constructor(rootStore, { parentViewStore }) {
		super(rootStore);
		makeObservable(this);
		this.parentView = parentViewStore;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.setCarrierServiceOptions();
		this.setWarehouseOptions();
		_.each(this.parentView.selectedItems, item => {
			this.form.$('fulfillmentOrders').add([
				{
					externalShipment: false,
					fulfillmentOrderId: item.fulfillmentOrderId,
					orderNumber: item.orderNumber,
					trackingNumber: item.trackingNumber,
					shippingCost: item.shippingCost,
					shippingCostSurcharge: item.shippingCostSurcharge,
					assignedWarehouse:
						item.warehouse && item.warehouseId ? { value: item.warehouseId, label: item.warehouse } : null,
					assignedCarrierService:
						item.carrierService && item.carrierServiceId
							? {
									value: item.carrierServiceId,
									label: item.carrierService,
									extra: { carrierId: item.carrierId },
							  }
							: null,
					Id: item.id,
				},
			]);
		});
		this.resume();
	};

	@action.bound
	setWarehouseOptions = async () => {
		this.warehouseOptions = [];
		let filter = {
			search: null,
			rpp: 15,
			page: 1,
			warehouseOnly: true,
			isOrganizationActive: true,
		};
		await this.applicationStore.find(filter);
		const newOptions = [];
		_.forEach(this.applicationStore.data, item => {
			newOptions.push({ value: item.id, label: item.applicationTitle });
		});
		this.warehouseOptions = newOptions;
	};

	@action.bound
	setCarrierServiceOptions = async () => {
		this.carrierServiceOptions = [];
		let filter = {
			search: null,
			rpp: 30,
			page: 1,
		};
		await this.carrierServiceStore.findCarrierService(filter);
		const newOptions = [];
		_.forEach(this.carrierServiceStore.data, item => {
			newOptions.push({ label: item.name, value: item.id });
		});
		this.carrierServiceOptions = newOptions;
	};

	@action.bound
	setIsExternal = async (evt, field) => {
		//getting the index number from Id field
		let index = field.id.slice(18, 19);
		if (evt.currentTarget.checked == true) {
			this.form.$('fulfillmentOrders').each(field => {
				let indexNumber = field.id.slice(18, 19);
				if (index == indexNumber) {
					field.set('rules', '');
					field.resetValidation();
				}
			});
		} else {
			this.form.$('fulfillmentOrders').each(field => {
				let indexNumber = field.id.slice(18, 19);
				if (index == indexNumber) {
					field.set('rules', 'required');
					if (field.id.includes('shippingCost')) {
						field.set('rules', 'required|numeric|decimalLength|numericMinValue');
					}
					if (field.id.includes('shippingCostSurcharge')) {
						field.set('rules', 'numeric|decimalLength|numericMinValue');
					}
					field.resetValidation();
				}
			});
		}
		this.form.$(field.path).value = evt.currentTarget.checked;
	};

	@action.bound
	setCurrentValue = async (field, value) => {
		field.set(value);
	};

	@action.bound
	markAsShippedBulk = async () => {
		const { fulfillmentOrders } = this.form.values();
		const fulfillmentOrderstoBeMarkedAsShipped = [];
		_.forEach(fulfillmentOrders, fulfillmentOrder => {
			const fulfillmentOrdertoBeMarkedAsShipped = {
				fulfillmentOrderId: fulfillmentOrder.Id,
				trackingNumber: fulfillmentOrder.externalShipment ? '' : fulfillmentOrder.trackingNumber,
				cost: fulfillmentOrder.externalShipment ? null : fulfillmentOrder.shippingCost,
				shippingCostSurcharge:
					fulfillmentOrder.externalShipment || fulfillmentOrder.shippingCostSurcharge == 0
						? 0
						: fulfillmentOrder.shippingCostSurcharge,
				assignedWarehouseId: fulfillmentOrder.externalShipment ? null : fulfillmentOrder.assignedWarehouse.value,
				assignedCarrierServiceId: fulfillmentOrder.externalShipment
					? null
					: fulfillmentOrder.assignedCarrierService.value,
				isInternal: !fulfillmentOrder.externalShipment,
			};
			fulfillmentOrderstoBeMarkedAsShipped.push(fulfillmentOrdertoBeMarkedAsShipped);
		});
		try {
			if (fulfillmentOrderstoBeMarkedAsShipped.length > 0) {
				this.parentView.selectedItems.forEach(o => delete o.isChecked);
				this.parentView.isSelectAllCheckboxSelected = false;
				this.parentView.selectedItems = [];
				if (this.parentView.selectedGroupByOption) {
					this.parentView.selectedGroups.forEach(g => delete g.isChecked);
					this.parentView.selectedGroups = [];
				}
			}
			this.parentView.markAsShippedModalBulk.close();

			const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.MARKASSHIPPED'));
			const response = await this.fulfillmentOrderStore.markAsShippedBulk(
				fulfillmentOrderstoBeMarkedAsShipped,
				sourceOfChange.markAsShippedBulkOrderList
			);
			this.rootStore.notificationStore.dismiss(notificationId);

			await this.parentView.findFulfillmentOrders();

			// Notifications.
			if (response.failed == 0 && response.successful > 0) {
				// If all is success.
				this.rootStore.notificationStore.success([response.message, { count: response.successful }]);
			} else if (response.failed > 0 && response.successful > 0) {
				// If errors occured.
				this.rootStore.notificationStore.warning([
					response.message,
					{ successful: response.successful, failed: response.failed },
				]);
			} else {
				// If all is fail.
				this.rootStore.notificationStore.error([response.message]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};
}

export default MarkAsShippedBulkViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/MarkAsShippedBulkViewStore.js